import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, Star, Users, Zap, ListChecks } from 'lucide-react';
import { useVendorPerformance } from '../../services/vendorAnalyticsService';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERIOD_TABS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

const VendorPerformance = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorPerformance(period);

  const performance = data?.data || {};

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return formatter(value);
  };

  const performanceMetrics = [
    {
      label: 'Response Time',
      value: formatValue(performance.response_time, (val) => `${Number(val).toFixed(1)} hrs`),
      icon: Clock,
      color: 'text-sky-600 dark:text-sky-400',
      bgColor: 'bg-sky-50 dark:bg-sky-950/40 border-sky-100 dark:border-sky-800/50',
    },
    {
      label: 'Completion Rate',
      value: formatValue(performance.completion_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: CheckCircle,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-800/50',
    },
    {
      label: 'Cancellation Rate',
      value: formatValue(performance.cancellation_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: XCircle,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-950/40 border-red-100 dark:border-red-800/50',
    },
    {
      label: 'Client Satisfaction',
      value: formatValue(performance.client_satisfaction, (val) => `${Number(val).toFixed(2)}/5`),
      icon: Star,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-800/50',
    },
    {
      label: 'Repeat Client Rate',
      value: formatValue(performance.repeat_client_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: Users,
      color: 'text-primary-600 dark:text-primary-400',
      bgColor: 'bg-primary-50 dark:bg-primary-950/40 border-primary-100 dark:border-primary-800/50',
    },
    {
      label: 'Service Efficiency',
      value: formatValue(performance.service_efficiency, (val) => `${Number(val).toFixed(1)}%`),
      icon: Zap,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-950/40 border-orange-100 dark:border-orange-800/50',
    },
  ];

  const cardClass =
    'rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6';

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Performance"
        subtitle="Track how your profile and services convert — ratings, bookings, and response trends."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.performance.title}
        steps={VENDOR_PAGE_GUIDES.performance.steps}
        icon={ListChecks}
      />

      <div
        className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
        role="tablist"
        aria-label="Performance period"
      >
        {PERIOD_TABS.map((tab) => {
          const isActive = period === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setPeriod(tab.value)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={`${cardClass} animate-pulse`}>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-300 px-4 py-3 text-sm">
          Failed to load performance data. Please try again.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performanceMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className={cardClass}>
                  <div
                    className={`w-12 h-12 border ${metric.bgColor} rounded-xl flex items-center justify-center mb-4`}
                  >
                    <Icon className={`w-6 h-6 ${metric.color}`} />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{metric.label}</p>
                  <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 tabular-nums">
                    {metric.value}
                  </p>
                </div>
              );
            })}
          </div>

          {performance.peak_hours && performance.peak_hours.length > 0 && (
            <div className={cardClass}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Peak Hours</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {performance.peak_hours.map((hour, index) => (
                  <div
                    key={index}
                    className="text-center rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-3"
                  >
                    <p className="text-sm text-gray-600 dark:text-gray-400">{hour.hour}</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                      {hour.bookings || 0}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">bookings</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorPerformance;
